import {
  Search,
  FilterList,
  Sort,
  CalendarToday,
  Science,
  Warning,
  CheckCircle,
} from "@mui/icons-material";
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  IconButton,
  Menu,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import { format, startOfDay, endOfDay } from "date-fns";
import React, { useState, useMemo, useEffect, useCallback } from "react";

import { patientBiomarkersApi } from "../../../apis";
import { BiomarkerResponseDto } from "../../../libs/cellera-sdk";
import {
  GetBiomarkersByPatientIdSortByEnum,
  GetBiomarkersByPatientIdSortOrderEnum,
  GetBiomarkersByPatientIdStatusEnum,
} from "../../../libs/cellera-sdk/apis/PatientBiomarkersApi";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";

interface PatientBiomarkersProps {
  patientId: string;
}

type SortField = GetBiomarkersByPatientIdSortByEnum;
type SortOrder = GetBiomarkersByPatientIdSortOrderEnum;
type StatusFilter = GetBiomarkersByPatientIdStatusEnum | "all";

function PatientBiomarkers({ patientId }: PatientBiomarkersProps) {
  const { colors } = useCelleraTheme();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortField>(
    GetBiomarkersByPatientIdSortByEnum.Date
  );
  const [orderBy, setOrderBy] = useState<SortOrder>(
    GetBiomarkersByPatientIdSortOrderEnum.Desc
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

  const isFilterMenuOpen = Boolean(filterAnchorEl);
  const isSortMenuOpen = Boolean(sortAnchorEl);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch biomarkers
  const {
    data: biomarkersListResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "biomarkers",
      patientId,
      debouncedSearchTerm,
      sortBy,
      orderBy,
      statusFilter,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const params: Parameters<
        typeof patientBiomarkersApi.getBiomarkersByPatientId
      >[0] = {
        patientId,
        search: debouncedSearchTerm || undefined,
        sortBy,
        sortOrder: orderBy,
        status: statusFilter !== "all" ? statusFilter : undefined,
        dateFrom: dateFrom ? startOfDay(dateFrom) : undefined,
        dateTo: dateTo ? endOfDay(dateTo) : undefined,
        page: 1,
        limit: 100,
      };
      return patientBiomarkersApi.getBiomarkersByPatientId(params);
    },
    enabled: Boolean(patientId),
  });

  const biomarkers = useMemo(
    () => biomarkersListResponse?.data || [],
    [biomarkersListResponse]
  );
  const totalResults = useMemo(
    () => (biomarkers as BiomarkerResponseDto[]).length,
    [biomarkers]
  );

  // Get status icon and color
  const getStatusIcon = useCallback(
    (outOfRange: boolean) => {
      if (outOfRange) {
        return <Warning sx={{ color: colors.error[500] }} />;
      }
      return <CheckCircle sx={{ color: colors.success[500] }} />;
    },
    [colors]
  );

  // Get status label
  const getStatusLabel = useCallback((outOfRange: boolean) => {
    return outOfRange ? "Out of Range" : "In Range";
  }, []);

  // Get status chip color
  const getStatusChipColor = useCallback(
    (outOfRange: boolean) => {
      if (outOfRange) {
        return {
          bgcolor: colors.error[50],
          color: colors.error[700],
          border: `1px solid ${colors.error[200]}`,
        };
      }
      return {
        bgcolor: colors.success[50],
        color: colors.success[700],
        border: `1px solid ${colors.success[200]}`,
      };
    },
    [colors]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    []
  );

  // Handle sort menu
  const handleSortMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setSortAnchorEl(event.currentTarget);
    },
    []
  );

  const handleSortMenuClose = useCallback(() => {
    setSortAnchorEl(null);
  }, []);

  const handleSortChange = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setOrderBy(order);
    setSortAnchorEl(null);
  }, []);

  // Handle filter menu
  const handleFilterMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setFilterAnchorEl(event.currentTarget);
    },
    []
  );

  const handleFilterMenuClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const handleStatusFilterChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setStatusFilter(event.target.value as StatusFilter);
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setStatusFilter("all");
    setDateFrom(null);
    setDateTo(null);
  }, []);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [statusFilter, dateFrom, dateTo]);

  // Check if sorting is active (not default)
  const isSortingActive = useMemo(() => {
    return (
      sortBy !== GetBiomarkersByPatientIdSortByEnum.Date ||
      orderBy !== GetBiomarkersByPatientIdSortOrderEnum.Desc
    );
  }, [sortBy, orderBy]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Search and Filter Bar */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search Input */}
          <TextField
            placeholder="Search by biomarker name"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ flex: 1, minWidth: "250px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.text.secondary }} />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Sort Button */}
          <IconButton
            onClick={handleSortMenuOpen}
            sx={{
              border: `1px solid ${colors.border.main}`,
              borderRadius: 1,
              color: isSortingActive
                ? colors.primary.main
                : colors.text.secondary,
            }}
          >
            <Sort />
          </IconButton>

          {/* Filter Button */}
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={handleFilterMenuOpen}
              sx={{
                border: `1px solid ${colors.border.main}`,
                borderRadius: 1,
                color:
                  activeFiltersCount > 0
                    ? colors.primary.main
                    : colors.text.secondary,
              }}
            >
              <FilterList />
            </IconButton>
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  height: 20,
                  minWidth: 20,
                  bgcolor: colors.primary.main,
                  color: "white",
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>
        </Box>

        {/* Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={isSortMenuOpen}
          onClose={handleSortMenuClose}
        >
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetBiomarkersByPatientIdSortByEnum.Date,
                GetBiomarkersByPatientIdSortOrderEnum.Desc
              )
            }
            selected={
              sortBy === GetBiomarkersByPatientIdSortByEnum.Date &&
              orderBy === GetBiomarkersByPatientIdSortOrderEnum.Desc
            }
          >
            Date (Newest First)
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetBiomarkersByPatientIdSortByEnum.Date,
                GetBiomarkersByPatientIdSortOrderEnum.Asc
              )
            }
            selected={
              sortBy === GetBiomarkersByPatientIdSortByEnum.Date &&
              orderBy === GetBiomarkersByPatientIdSortOrderEnum.Asc
            }
          >
            Date (Oldest First)
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetBiomarkersByPatientIdSortByEnum.Name,
                GetBiomarkersByPatientIdSortOrderEnum.Asc
              )
            }
            selected={
              sortBy === GetBiomarkersByPatientIdSortByEnum.Name &&
              orderBy === GetBiomarkersByPatientIdSortOrderEnum.Asc
            }
          >
            Biomarker Name (A-Z)
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetBiomarkersByPatientIdSortByEnum.Name,
                GetBiomarkersByPatientIdSortOrderEnum.Desc
              )
            }
            selected={
              sortBy === GetBiomarkersByPatientIdSortByEnum.Name &&
              orderBy === GetBiomarkersByPatientIdSortOrderEnum.Desc
            }
          >
            Biomarker Name (Z-A)
          </MenuItem>
        </Menu>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={isFilterMenuOpen}
          onClose={handleFilterMenuClose}
          PaperProps={{
            sx: {
              width: 320,
              p: 2,
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>

            {/* Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value={GetBiomarkersByPatientIdStatusEnum.InRange}>
                  In Range
                </MenuItem>
                <MenuItem value={GetBiomarkersByPatientIdStatusEnum.OutOfRange}>
                  Out of Range
                </MenuItem>
              </Select>
            </FormControl>

            {/* Date Range Filter */}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Date Range
            </Typography>
            <DatePicker
              label="From Date"
              value={dateFrom}
              onChange={(newValue) => setDateFrom(newValue)}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
            <DatePicker
              label="To Date"
              value={dateTo}
              onChange={(newValue) => setDateTo(newValue)}
              maxDate={new Date()}
              minDate={dateFrom || undefined}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />

            {/* Clear Filters Button */}
            {activeFiltersCount > 0 && (
              <Box
                onClick={handleClearFilters}
                sx={{
                  cursor: "pointer",
                  color: colors.primary.main,
                  textAlign: "center",
                  mt: 1,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                <Typography variant="body2">Clear All Filters</Typography>
              </Box>
            )}
          </Box>
        </Menu>

        {/* Total Results */}
        {!isLoading && !error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {totalResults} {totalResults === 1 ? "result" : "results"} found
            </Typography>
          </Box>
        )}

        {/* Biomarkers List */}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              Loading...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: colors.error[500] }}>
              Error loading biomarkers
            </Typography>
          </Box>
        ) : biomarkers.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              No biomarkers found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {biomarkers.map((biomarker) => (
              <Card
                key={biomarker.id}
                sx={{
                  border: `1px solid ${colors.border.main}`,
                  borderRadius: 2,
                  "&:hover": {
                    boxShadow: 2,
                    cursor: "pointer",
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {/* Biomarker Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: colors.background.paper,
                      }}
                    >
                      <Science sx={{ color: colors.primary.main }} />
                    </Box>

                    {/* Biomarker Info */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: biomarker.outOfRange ? 700 : 600,
                          color: biomarker.outOfRange
                            ? colors.error[700]
                            : colors.text.primary,
                          mb: 0.5,
                        }}
                      >
                        {biomarker.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={getStatusLabel(biomarker.outOfRange || false)}
                          size="small"
                          icon={getStatusIcon(biomarker.outOfRange || false)}
                          sx={getStatusChipColor(biomarker.outOfRange || false)}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: colors.text.secondary,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {biomarker.value} {biomarker.unit}
                          </Typography>
                        </Box>
                        {biomarker.referenceRange && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: colors.text.secondary,
                            }}
                          >
                            <Typography variant="body2">
                              Range: {biomarker.referenceRange}
                            </Typography>
                          </Box>
                        )}
                        {biomarker.date && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: colors.text.secondary,
                            }}
                          >
                            <CalendarToday sx={{ fontSize: 16 }} />
                            <Typography variant="body2">
                              {format(new Date(biomarker.date), "dd/MM/yyyy")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default PatientBiomarkers;
